import { Button, ButtonType, cssX, FCAdditionalProps, FCX, getRem } from "@datassential/platform-ui-lib";
import { RespondentType } from "@models/RespondentType";
import { DropdownProps } from "@components/Dropdown/Dropdown";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useCommonDataContext, useRespondentType, useRespondentTypesGroups } from "@core/CommonDataContext";
import cn from "classnames";
import {
    DEFAULT_SELECT_AMONG_DATA, SelectAmongData,
    useSelectAmongContext,
    useSelectAmongData
} from "@vms/SelectAmong/SelectAmongContext";
import Field from "@components/Field";
import { IoIosArrowDown } from "react-icons/io";
import Modal from "@components/Modal";
import { useIsUserBrandsSelected } from "@vms/SelectAmong/useIsUserBrandsSelected";
import SelectAmongUserBrands from "@vms/SelectAmong/SelectAmongUserBrands";
import SelectAmongRespondentTypes, {
    SelectAmongRespondentTypesItem
} from "@vms/SelectAmong/SelectAmongRespondentTypes";
import { useGetSelectedOptionsNames } from "@vms/SelectAmong/useSelectedOptionsNames";
import { toggleInArray } from "@core/utils/array";
import { toast } from "react-toastify";
import { deepEqual } from "@core/utils/object";

export const SELECTED_OPTIONS_LIMIT = 11;

export type SelectAmongProps = FCAdditionalProps & {
    label?: ReactNode;
    place?: DropdownProps["place"];
    fieldClassName?: string;
    defaultThumbnailText?: ReactNode;
    showTotalOption?: boolean;
    useGroupsLimit?: boolean;
    useOptionsLimit?: boolean;
    useCoExclusions?: boolean;
    noBrands?: boolean;
    noRespondentTypes?: boolean;
    allowMultiBrands?: boolean;
    initialData?: Partial<SelectAmongData>;
    onApply?: () => void;
};

const SelectAmong: FCX<SelectAmongProps> = ({
    label,
    className,
    fieldClassName,
    defaultThumbnailText = 'Total US',
    showTotalOption = false,
    useGroupsLimit = false,
    useOptionsLimit = false,
    useCoExclusions = false,
    noBrands = false,
    noRespondentTypes = false,
    allowMultiBrands,
    initialData,
    onApply,
}) => {
    const totalUsOption = useRespondentType(RespondentType.TotalUS);

    const {
        data,
        setPartial,
    } = useSelectAmongContext();
    const { isLoading } = useCommonDataContext();


    const [isOpened, setIsOpened] = useState(false);
    const [localData, setPartialLocalData] = useSelectAmongData();

    const getSelectedOptionsNames = useGetSelectedOptionsNames();

    const selectedOptionsNames = useMemo(
        () => getSelectedOptionsNames(localData),
        [getSelectedOptionsNames, localData]
    );

    const {
        respondentTypes: localRespondentTypes,
    } = localData;

    const originalRespondentTypesGroups = useRespondentTypesGroups();

    const selectedGroups = useMemo(
        () => originalRespondentTypesGroups.filter(
            (group) => group.items.some(
                (item) => localRespondentTypes.includes(item.id as RespondentType)
            )
        ),
        [originalRespondentTypesGroups, localRespondentTypes]
    );

    const isUserBrandsSelected = useIsUserBrandsSelected(localData);

    const isGroupsLimitReached = useMemo(
        () => {
            if (useGroupsLimit) {
                return selectedGroups.length > 1 || (
                    selectedGroups.length === 1 && isUserBrandsSelected
                );
            }

            return false;
        },
        [selectedGroups.length, isUserBrandsSelected, useGroupsLimit]
    );

    const dispatchPartialLocalData = useCallback(
        (partial: Partial<typeof localData>) => {
            const next = { ...localData, ...partial };
            const selectedOptionsNames = getSelectedOptionsNames(next);

            if (selectedOptionsNames.length > SELECTED_OPTIONS_LIMIT && useOptionsLimit) {
                toast.warning(`You can select up to ${SELECTED_OPTIONS_LIMIT} options.`);
                return {};
            }

            setPartialLocalData(partial);
        },
        [getSelectedOptionsNames, localData, setPartialLocalData, useOptionsLimit]
    );

    useEffect(() => {
        setPartialLocalData(data);
    }, [data, setPartialLocalData]);

    const thumbnailText = useMemo(
        () => {
            if (selectedOptionsNames.length === 0) {
                return <>{defaultThumbnailText}</>;
            } else if (selectedOptionsNames.length === 1) {
                return <>{selectedOptionsNames[0]}</>;
            } else {
                return <>{selectedOptionsNames.length} options</>;
            }

        },
        [selectedOptionsNames, defaultThumbnailText]
    );

    const cancel = useCallback(
        () => {
            setIsOpened(false);
            dispatchPartialLocalData(data);
        },
        [data, dispatchPartialLocalData, setIsOpened]
    );

    const apply = useCallback(
        () => {
            setIsOpened(false);
            onApply?.();
            setPartial(localData);
        },
        [localData, setPartial, setIsOpened, onApply]
    );

    const isDataChanged = useMemo(
        () => !deepEqual(localData, data),
        [localData, data]
    );

    const resetAll = useCallback(
        () => {
            dispatchPartialLocalData({ ...DEFAULT_SELECT_AMONG_DATA });
        },
        [dispatchPartialLocalData]
    );

    const footerText = useMemo(
        () => {
            if (useGroupsLimit) {
                return "Select option(s) from up to 2 demographic groups";
            } else if (useOptionsLimit) {
                return `Select up to ${SELECTED_OPTIONS_LIMIT} options`;
            } else {
                return '';
            }
        },
        [useGroupsLimit, useOptionsLimit]
    );

    useEffect(() => {
        if (initialData) {
            setPartial(initialData);
        }
    }, [initialData, setPartial]);

    if (isLoading) {
        return null;
    }

    return (
        <>
            <Field
                className={cn(fieldClassName)}
                label={label}
                modifiers={['min-width']}
            >
                <div
                    onClick={() => setIsOpened(!isOpened)}
                    data-testid="selectThumbnailInput"
                    className={cn(
                        "SelectComponent__thumbnail",
                        "Indicatable",
                        {
                            "is-applied": selectedOptionsNames.length > 0,
                        },
                    )}
                >
                    <div className="SelectComponent__thumbnail-inner">
                        {thumbnailText}
                    </div>
                    <IoIosArrowDown className="SelectComponent__thumbnail-icon"/>
                </div>
            </Field>
            {isOpened && (
                <Modal
                    onClose={cancel}
                    modifiers={['no-pad']}
                    className={
                        cn(
                            "SelectAmong",
                            className
                        )
                    }
                    style={{
                        minWidth: getRem(600),
                    }}
                >
                    <div className="SelectAmong__modal">
                        <div className="SelectAmong__cols">
                            {!noRespondentTypes && (
                                <SelectAmongRespondentTypes
                                    value={localRespondentTypes}
                                    dispatchPartial={dispatchPartialLocalData}
                                    isGroupsLimitReached={isGroupsLimitReached}
                                    showTotalOption={showTotalOption}
                                    useCoExclusions={useCoExclusions}
                                />
                            )}
                            {!noBrands && (
                                <div className="SelectAmong__col">
                                    {noRespondentTypes && showTotalOption && (
                                        <SelectAmongRespondentTypesItem
                                            id={RespondentType.TotalUS}
                                            name={totalUsOption?.name ?? ''}
                                            value={localRespondentTypes}
                                            onChange={() => dispatchPartialLocalData({
                                                respondentTypes: toggleInArray(
                                                    localRespondentTypes,
                                                    RespondentType.TotalUS as RespondentType
                                                ),
                                            })}
                                            isGroupsLimitReached={isGroupsLimitReached}
                                        />
                                    )}
                                    <div className={cn(
                                        cssX("Dropdown__group"),
                                        "SelectAmong__disable-element",
                                        { 'is-disabled': isGroupsLimitReached && selectedGroups.length > 1 },
                                    )}>
                                        <div
                                            className={cn(
                                                cssX("Dropdown__group-title"),
                                                "SelectAmong__group-title",
                                            )}
                                            data-testid={`textRespondentGroupTitle${3}_${1}`}
                                        >
                                            Users
                                        </div>
                                        <div className={cssX("Dropdown__group-content")}>
                                            <SelectAmongUserBrands
                                                data={localData}
                                                dispatchPartial={dispatchPartialLocalData}
                                                useCoExclusions={useCoExclusions}
                                                allowMultiSelect={allowMultiBrands}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="Modal__footer">
                            <div>
                                {footerText}
                            </div>
                            <div className="Modal__footer-part">
                                <Button
                                    type={ButtonType.Button}
                                    modifiers={["naked"]}
                                    onClick={resetAll}
                                >
                                    Clear All
                                </Button>
                                <Button
                                    type={ButtonType.Button}
                                    onClick={isDataChanged ? apply : cancel}
                                >
                                    Apply
                                </Button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default SelectAmong;